import { handleSaveOnboardingSettings, onboardingSettingSelector } from '@/redux/slice/onboarding.slice';
import { BlockStack, Button, Card, InlineGrid, Link, Select, SkeletonBodyText } from '@shopify/polaris';

import CartIcon from '@/asset/icons/cart';
import BoldText from '@/components/BoldText';
import CustomHorizonRadio from '@/components/CustomHorizonRadio';
import RegularText from '@/components/RegularText';
import { config } from '@/config';
import { ONBOARDING_STEP } from '@/constants/enum';
import { THUMBNAIL } from '@/helpers/link';
import { useUpdateEnableScheduleMutation } from '@/redux/query';
import { IResponseApi } from '@/types/apis/response';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SelectStyled } from '../onboard-styled';
import { ButtonContainerStyled, ImgStyled, RadioTabStyled } from '../styled';
import { XIcon } from '@shopify/polaris-icons';

interface IProps {
  shopThemesData: IResponseApi.IGetShopThemes[] | undefined;
  isLoadingShopThemes: boolean;
  handleSkip?: () => void;
}

const OnboardingWelcome = ({ shopThemesData, isLoadingShopThemes, handleSkip }: IProps) => {
  const { t } = useTranslation('onboarding');
  const onboardingSettings = useSelector(onboardingSettingSelector);
  const dispatch = useDispatch();

  const [updateEnableSchedule, { error: updateEnableScheduleError }] = useUpdateEnableScheduleMutation();

  const themeOptions = useMemo(
    () =>
      shopThemesData?.map((item) => ({
        value: item.id,
        label: `${item.name} ${item.role === 'MAIN' ? '(Live theme)' : ''}`,
      })),
    [shopThemesData],
  );

  const handleNextStep = () => {
    dispatch(handleSaveOnboardingSettings({ step: ONBOARDING_STEP.EMBEDDED_APP }));
  };

  useEffect(() => {
    updateEnableSchedule({
      shop: config.shop,
      isEnablePickup: onboardingSettings.isEnablePickup,
      isEnableDelivery: onboardingSettings.isEnableDelivery,
      isEnableShipping: onboardingSettings.isEnableShipping,
    })
      .unwrap()
      .catch(() => console.log('error:', updateEnableScheduleError));
  }, [
    onboardingSettings.isEnableDelivery,
    onboardingSettings.isEnablePickup,
    onboardingSettings.isEnableShipping,
    updateEnableSchedule,
    updateEnableScheduleError,
  ]);

  return (
    <Card>
      <div className="flex justify-end">
        <Button icon={XIcon} variant="tertiary" onClick={handleSkip}></Button>
      </div>
      <InlineGrid columns={{ xs: 1, lg: 2 }} gap={'400'}>
        <ImgStyled className="welcome-img" height={'500px'}>
          <img src={THUMBNAIL.ONBOARDING_IMG} alt="welcome-image" />
        </ImgStyled>
        <div className="mt-16 flex flex-col justify-between">
          <div>
            <BlockStack gap={'300'}>
              <div className="flex items-center gap-8">
                <CartIcon width={24} height={22} />
                <BoldText variant="headingXl">{t('welcome.title')}</BoldText>
              </div>
              <RegularText tone="subdued">{t('welcome.desc')}</RegularText>
            </BlockStack>

            <div style={{ marginTop: '30px' }}>
              {isLoadingShopThemes ? (
                <SkeletonBodyText lines={2} />
              ) : (
                <SelectStyled>
                  <Select
                    label={t('welcome.theme.label')}
                    helpText={
                      <div>
                        <span>{t('welcome.theme.helpText.text')}</span>{' '}
                        <Link
                          url="https://docs.dingdoong.io/onboarding/how-to-create-a-test-theme"
                          target="_blank"
                          removeUnderline
                        >
                          {t('welcome.theme.helpText.link')}
                        </Link>
                      </div>
                    }
                    options={themeOptions}
                    value={onboardingSettings.theme}
                    onChange={(value) => dispatch(handleSaveOnboardingSettings({ theme: value }))}
                    disabled={isLoadingShopThemes}
                  />
                  <RegularText tone="subdued">{t('welcome.theme.note')}</RegularText>
                </SelectStyled>
              )}
            </div>
          </div>

          <ButtonContainerStyled>
            <RadioTabStyled>
              <CustomHorizonRadio
                checked={onboardingSettings.step}
                onChange={(value) => dispatch(handleSaveOnboardingSettings({ step: +value }))}
                options={[ONBOARDING_STEP.WELCOME, ONBOARDING_STEP.EMBEDDED_APP]}
              />
            </RadioTabStyled>
            <Button variant="primary" onClick={() => handleNextStep()}>
              {t('welcome.start')}
            </Button>
          </ButtonContainerStyled>
        </div>
      </InlineGrid>
    </Card>
  );
};

export default OnboardingWelcome;
